import { Box } from '@chakra-ui/react';
import { GenericContentBlockConfig, GenericContentBlocks } from '@src/components/sco/GenericLP/GenericContentBlock';
import GenericFAQ from '@src/components/sco/GenericLP/GenericFAQ';
import GenericHero from '@src/components/sco/GenericLP/GenericHero';
import GenericSection from '@src/components/sco/GenericLP/GenericSection';
import GenericStats, { GenericStatItem } from '@src/components/sco/GenericLP/GenericStats';
import { PitchDeckLandingDiagnostic, type LandingDiagnosticHandle } from '@src/components/sco/PitchDeck/PitchDeckLandingDiagnostic';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect.tsx';
import { TrackEvent } from '@src/lib/TrackEvent.tsx';
import React, { useCallback, useMemo, useRef } from 'react';
import { LuChartLine, LuRocket, LuUsers } from 'react-icons/lu';
import GenericImage from '../../GenericLP/GenericImage';
const variantKey = 'pitch-deck';
const SECTION_BG = 'colorPalette.bg';

// ─── Page content (edit here) ─────────────────────────────────────────────────

const stats: GenericStatItem[] = [{
  icon: LuRocket,
  text: 'Over 1 Million Startups'
}, {
  icon: LuUsers,
  text: '$20B Raised by our Startups'
}, {
  icon: LuChartLine,
  text: '$1B+ Capital Commitments'
}, {
  icon: LuRocket,
  text: '15 IPOs'
}, {
  icon: LuUsers,
  text: '15+ Years of Service'
}];
const buildMatchingBlocks = (openDiagnostic: () => void): GenericContentBlockConfig[] => [{
  eyebrow: 'Comprehensive Deck Audit',
  heading: 'Every answer in 2 minutes.',
  paragraph: 'Upload once; we audit your entire deck against what investors actually look for.',
  primaryCta: {
    label: 'Audit My Deck',
    onClick: openDiagnostic
  },
  listItems: ['Your entire deck, audited', 'Takes about 2 minutes', 'Your deck stays private'],
  visual: <GenericImage src={'/assets/illustration/pitchdecks/pitch-deck-1-audit.svg'} />
}, {
  eyebrow: 'Investor-Grade Score',
  heading: 'What investors need to hear.',
  paragraph: 'Grades your deck against exactly what investors expect to see, and what makes them invest.',
  listItems: ['Graded on what investors expect to see', 'Feedback built from what makes them invest', 'A grade, not a vanity score'],
  primaryCta: {
    label: 'Audit My Deck',
    onClick: openDiagnostic
  },
  visual: <GenericImage src={'/assets/illustration/pitchdecks/pitch-deck-2-grade.svg'} />
}, {
  eyebrow: 'Actual Rewrites',
  heading: 'Not suggestions, actual rewrites.',
  paragraph: "We take what you're trying to say and translate it into what an investor needs to hear, paste-ready copy, not a to-do list.",
  listItems: ['We rewrite the slide for you', 'Your message, translated into investor language', 'Paste-ready, not a punch list'],
  primaryCta: {
    label: 'Audit My Deck',
    onClick: openDiagnostic
  },
  visual: <GenericImage src={'/assets/illustration/pitchdecks/pitch-deck-3-rewrites.svg'} />
}, {
  eyebrow: 'Proven Track Record',
  heading: "Advice that's raised over $1B.",
  paragraph: "The same deck guidance that's helped startups on our platform raise over $1B in real funding commitments.",
  listItems: ['Over $1B in real funding commitments', 'Raised by decks we helped shape', 'Not theory, a track record'],
  primaryCta: {
    label: 'Audit My Deck',
    onClick: openDiagnostic
  },
  visual: <GenericImage src={'/assets/illustration/pitchdecks/pitch-deck-4-track-record.svg'} />
}, {
  eyebrow: 'Live Expert Help',
  heading: 'Live experts, not just AI.',
  paragraph: 'Real pitch experts are on call to review your deck and answer questions, AI speed, human judgment.',
  listItems: ['Live deck experts on call', 'Not just AI suggestions', 'Get tough love'],
  primaryCta: {
    label: 'Audit My Deck',
    onClick: openDiagnostic
  },
  visual: <GenericImage src={'/assets/illustration/pitchdecks/pitch-deck-5-live-experts.svg'} />
}];
const faqItems = [{
  question: 'What is an Investor Grade?',
  answer: 'A single 0-100 score for how investor-ready your deck is, backed by a slide-by-slide breakdown of what’s working and what’s dragging you down.'
}, {
  question: 'How do you score my deck?',
  answer: 'We grade each core slide (Problem, Solution, Market Size, Traction, and more) against what investors actually look for, then roll it up into your overall grade.'
}, {
  question: 'Do you rewrite my slides for me?',
  answer: 'Wil walks you through each weak slide and rebuilds it with you, so you leave with stronger slides and understand why they’re stronger.'
}, {
  question: 'What format does my deck need to be in?',
  answer: 'Currently, only PDF files are supported. Upload your PDF and we will take it from there.'
}, {
  question: 'Is it really free to get a grade?',
  answer: 'Yes. Upload your deck and get your Investor Grade and top fixes at no cost.'
}];

// ─── Page ─────────────────────────────────────────────────────────────────────

export const PitchDeckLandingSection = () => {
  const activeContent = useActiveContentContext();
  const diagnosticRef = useRef<LandingDiagnosticHandle>(null);
  const openDiagnostic = useCallback(() => {
    diagnosticRef.current?.open();
  }, []);
  const matchingBlocks = useMemo(() => buildMatchingBlocks(openDiagnostic), [openDiagnostic]);
  useUniversalEffect(() => {
    activeContent.setSiteSection('pitch-decks.landing');
    activeContent.setItem({
      __typename: 'LandingPage',
      title: 'Pitch Decks. Funded.',
      description: 'Stop guessing. Get an investor-grade audit of your pitch deck — scores, objections, and rewrites for the slides that cost you meetings.'
    });
    return () => {
      activeContent.setItem(null);
    };
  }, []);
  return <Box color={'fg'} width={'full'}>
      <TrackEvent action={'lpViewed'} onlyOnScreen={true} traits={{
      variantKey
    }} />

      <GenericHero gradientText={'Funded.'} heading={'Pitch Decks. Funded.'} subheading={'Stop guessing. We’ll tell you exactly what Investors want to fund your specific deck.'}>
        <PitchDeckLandingDiagnostic ref={diagnosticRef} />
      </GenericHero>

      <GenericStats background={'colorPalette.container/50'} heading={'Key Stats'} stats={stats} />

      <GenericSection background={SECTION_BG} heading={'See Your Deck Through an Investor’s Eyes'} subheading={'Upload your deck and we’ll grade every slide and show you exactly what to fix first.'}>
        <GenericContentBlocks blocks={matchingBlocks} />
      </GenericSection>

      <GenericFAQ heading={'Frequent Questions'} items={faqItems} subheading={"Want to know more? Here's some common questions & answers."} />
    </Box>;
};